import axios, { AxiosError, Canceler } from 'axios';
import {
  ApiPaths,
  ApiPaths_Chat,
  ApiPaths_Groups,
  ApiPaths_Notification,
  ApiPaths_Post,
  IFriendResponse,
  IGroupHeader,
  ISaveUserEducationDetailsRequest,
  ISaveUserEducationDetailsResponse,
  ISaveUserExperienceDetailsRequest,
  ITopic,
  IUserDetails,
  IUserSummaryRequest,
  IAdminDeleteRequest,
  IExternalLinkPayload,
  IExternalLinkResponse
} from 'models/apiPaths';
import {
  IOtpConfirmation,
  IUserRegisterByMobile,
  IUserRegisterResponse
} from 'models/authentication';
import { IGroupeSearchRequest } from 'models/groups';
import {
  ICommentsPost,
  IPostContent,
  IUserPostVoteRequest,
  IUserPollVoteRequest
} from 'models/myfeed';
import {
  IFriendNotificationAcceptRejectRequest,
  IFriendNotificationResponse,
  INotificationCountResponse,
  INotificationSingleReadRequest,
  ITurnOffNotificationRequest
} from 'models/notification';
import { IReportPostRequest, IReportPostResponse } from 'models/reports';
import {
  BASE_API,
  axiosPost,
  axiosGet,
  axiosPut,
  axiosDelete,
  axiosPatch,
  BASE_API_POST,
  BASE_API_ADMIN,
  BASE_API_INTERACTION,
  BASE_API_NOTIFICATION,
  BASE_API_CHAT
} from 'utilities/axiosManagerFunctions';

export const getAPIError = (err: any) => {
  /* istanbul ignore next */
  const errorMessage = err.message || err.toString();
  return new AxiosError(
    `API Error: "${errorMessage}"`,
    undefined,
    undefined,
    undefined,
    err?.response
  );
};

export const axiosGroupJoin = async (groupId: any): Promise<any> => {
  try {
    const response = await axiosPut(
      ApiPaths_Groups.GROUP_JOIN + groupId,
      {},
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosGroupUnjoin = async (groupId: any): Promise<any> => {
  try {
    const response = await axiosPut(
      ApiPaths_Groups.GROUP_UNJOIN + groupId,
      {},
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosPostGroupsList = async (
  payload: any,
  postId: string
): Promise<any> => {
  try {
    const response = await axiosPost(
      `${ApiPaths_Groups.USER_GROUPS_LIST}/${postId}`,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostLoginWithEmail = async (
  payload: {
    email: string;
    password: string;
  },
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost(
      ApiPaths.LOGIN_EMAIL,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      },
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosUserRegisterByEmail = async (
  payload: any,
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths.SIGNUP_EMAIL,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      },
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosUserRegisterByMobile = async (
  payload: IUserRegisterByMobile,
  onInit?: (canceler: Canceler) => void
): Promise<IUserRegisterResponse> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths.SIGNUP_MOBILE,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      },
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostOTPVerificationFromLogin = async (
  apiPath: string,
  payload: IOtpConfirmation,
  onInit?: (canceler: Canceler) => void
): Promise<IOtpConfirmation> => {
  try {
    const response = await axiosPost<any>(
      apiPath,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      },
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostOTPResend = async (
  apiPath: string,
  payload: IOtpConfirmation,
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      apiPath,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      },
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostForgotRequestEmail = async (
  apiPath: string,
  payload: {
    email: string;
    captchaToken?: string;
  },
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      apiPath,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      },
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosPostForgotPasswordVerifyOTP = async (
  apiPath: string,
  payload: {
    email: string;
    code: string;
  },
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      apiPath,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      },
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosPostResetPasswordVerify = async (
  apiPath: string,
  payload: {
    email: string;
    code: string;
    newPassword: string;
  },
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      apiPath,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      },
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosTrendingFeed = async (
  apiPath: string,
  payload: any,
  onInit?: (canceler: Canceler) => void
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      apiPath,
      payload,
      {
        cancelToken: new axios.CancelToken((canceler) => {
          onInit?.(canceler);
        })
      }
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetTrendingUsers = async (): Promise<any> => {
  try {
    const response = await axiosGet<any>(
      ApiPaths.TRENDING_USERS,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostMyFeed = async (
  apiPath: string,
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(apiPath, payload, {});
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostMyFeedPostId = async (payload: any): Promise<any> => {
  try {
    const ApiPath = ApiPaths.GET_POST + '/' + payload.postId;
    const response = await axiosGet<IPostContent>(ApiPath, {});
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetVoteList = async (userId: string): Promise<any> => {
  try {
    const ApiPath = ApiPaths.VOTE_USERS + '/' + userId;
    const response = await axiosGet<any>(ApiPath, {}, BASE_API_INTERACTION);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostForVote = async (
  payload: IUserPostVoteRequest
): Promise<any> => {
  try {
    const response = await axiosPost(
      ApiPaths.POST_VOTE,
      payload,
      {},
      BASE_API_INTERACTION
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostMyGroups = async (
  urlPath: string,
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(urlPath, payload, {});
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCreatePost = async (
  path: string,
  payload: FormData
): Promise<FormData> => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  try {
    const response = await axiosPost<any>(path, payload, config);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCreatePoll = async (
  path: string,
  payload: FormData
): Promise<FormData> => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  try {
    const response = await axiosPost<any>(path, payload, config);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosUpdatePollVote = async (
  payload: IUserPollVoteRequest
): Promise<any> => {
  try {
    const response = await axiosPost(
      ApiPaths.UPDATE_POLL_VOTE,
      payload,
      {},
      BASE_API_INTERACTION
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostGroupSearch = async (
  path: string,
  payload: IGroupeSearchRequest
): Promise<any> => {
  try {
    const response = await axiosPost(path, payload, {}, BASE_API_POST);
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const fetchUserDetails = async (
  apiPath: string
): Promise<IUserDetails> => {
  try {
    const response = await axiosGet<IUserDetails>(apiPath, {}, BASE_API);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserProfile = async (
  payload: IUserSummaryRequest
): Promise<ISaveUserEducationDetailsResponse> => {
  try {
    const response = await axiosPut<any>(
      ApiPaths.USER_PROFILE_SUMMARY,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const saveUserEducationDetails = async (
  apiPath: string,
  payload: ISaveUserEducationDetailsRequest
): Promise<any> => {
  try {
    const response = await axiosPut<ISaveUserEducationDetailsRequest>(
      apiPath,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const deleteUserEducationDetails = async (
  apiPath: string,
  payload: ISaveUserEducationDetailsRequest
): Promise<any> => {
  try {
    const response = await axiosDelete(
      apiPath,
      {
        data: payload
      },
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const saveUserExperienceDetails = async (
  apiPath: string,
  payload: ISaveUserExperienceDetailsRequest
): Promise<any> => {
  try {
    const response = await axiosPut<ISaveUserExperienceDetailsRequest>(
      apiPath,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const deleteUserExperienceDetails = async (
  apiPath: string,
  payload: ISaveUserExperienceDetailsRequest
): Promise<any> => {
  try {
    const response = await axiosDelete(
      apiPath,
      {
        data: payload
      },
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const fetchInterestTopics = async (): Promise<ITopic[]> => {
  try {
    const response = await axiosGet<ITopic[]>(
      ApiPaths.METADATA_CONTROLLER,
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status !== 200) {
      throw getAPIError(error);
    }
    throw new Error(error.message || 'An error occurred while fetching topics');
  }
};

export const fetchPollCategory = async (): Promise<ITopic[]> => {
  try {
    const response = await axiosGet<ITopic[]>(
      ApiPaths.METADATA_CONTROLLER_POLL_CATEGORY,
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status !== 200) {
      throw getAPIError(error);
    }
    throw new Error(error.message || 'An error occurred while fetching poll category');
  }
};

export const updateInterestTopics = async (
  selectedIDs: string[]
): Promise<any> => {
  try {
    const response = await axiosPut(
      ApiPaths.UPDATE_USER_INTERESTS,
      {
        id: selectedIDs
      },
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const updateBio = async (bio: string): Promise<any> => {
  try {
    const response = await axiosPut(
      ApiPaths.USER_BIO_UPDATE,
      {
        userBio: bio
      },
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosGetGroupPost = async (
  payload: string,
  path: string
): Promise<any> => {
  try {
    const response = await axiosGet<any>(path + payload, {}, BASE_API_POST);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeleteGroup = async (groupId: string): Promise<any> => {
  try {
    const response = await axiosPatch(
      `group/delete/${groupId}`,
      {},
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export async function fetchFriends(): Promise<IFriendResponse[]> {
  try {
    const response = await axiosGet<IFriendResponse[]>(
      ApiPaths.FRIEND,
      {},
      BASE_API
    );
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch friends list');
  }
}

export const axiosGetFriendRequestList = async (): Promise<
  IFriendNotificationResponse[]
> => {
  try {
    const response = await axiosGet<IFriendNotificationResponse[]>(
      ApiPaths.FRIEND_REQUEST,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostFriendRequestList = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths.FRIEND_PAGE_RESPONSE,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPutFriendRequest = async (
  payload: IFriendNotificationAcceptRejectRequest
): Promise<any> => {
  try {
    const response = await axiosPut(
      ApiPaths.FRIEND_RESPONSE,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const postFriendRequest = async (
  payload: any,
  apiPath: string
): Promise<any> => {
  try {
    const response = await axiosPost(apiPath, payload, {}, BASE_API);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const postNotificationGeneralList = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Notification.NOTIFICATION_LIST_PAGE,
      payload,
      {},
      BASE_API_NOTIFICATION
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const postNotificationReadAll = async (): Promise<any> => {
  try {
    const response = await axiosPost(
      ApiPaths_Notification.NOTIFICATION_READ_ALL,
      {},
      {},
      BASE_API_NOTIFICATION
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const postNotificationRead = async (
  payload: INotificationSingleReadRequest
): Promise<any> => {
  try {
    const response = await axiosPost(
      ApiPaths_Notification.NOTIFICATION_READ,
      payload,
      {},
      BASE_API_NOTIFICATION
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const deleteNotification = async (
  notificationID: string
): Promise<any> => {
  try {
    const response = await axiosDelete(
      `${ApiPaths_Notification.NOTIFICATION_DELETE}/${notificationID}`,
      {},
      BASE_API_NOTIFICATION
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const putTurnOffNotification = async (
  payload: ITurnOffNotificationRequest
): Promise<any> => {
  try {
    const response = await axiosPut<any>(
      ApiPaths_Notification.NOTIFICATION_OFF,
      payload,
      {},
      BASE_API_NOTIFICATION
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosPostBlockUnblock = async (
  apiPath: string,
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost(apiPath, payload, {}, BASE_API);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const sendPostReport = async (
  payload: IReportPostRequest
): Promise<IReportPostResponse> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Post.REPORT_POST,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostGroupAutocomplete = async (
  keyword: string
): Promise<any> => {
  try {
    const response = await axiosPost(
      '/group/autocomplete',
      { keyword },
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosRePost = async (payload: any): Promise<any> => {
  try {
    const response = await axiosPost(
      ApiPaths_Post.REPOST_POST,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeletePost = async (payload: any): Promise<any> => {
  try {
    const response = await axiosDelete(ApiPaths.POST_REMOVE, {
      data: payload
    });
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosPostUserSearch = async (payload: {
  keyword: string;
}): Promise<any> => {
  try {
    const response = await axiosPost(
      ApiPaths.USER_SEARCH,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosPostUserDeleteRequest = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost(
      ApiPaths.USER_DELETE_REQUEST,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeleteAdminRemoveUser = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosDelete(
      ApiPaths.ADMIN_REMOVE_USER,
      {
        data: payload
      },
      BASE_API_ADMIN
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostCommentsPostId = async (
  payload: any,
  postId: string
): Promise<any> => {
  try {
    const response = await axiosPost(
      `${ApiPaths.COMMENTS_POST_ID_PAGINATION}/${postId}`,
      payload,
      {},
      BASE_API_INTERACTION
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostComment = async (
  payload: ICommentsPost
): Promise<any[]> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths.COMMENTS,
      payload,
      {},
      BASE_API_INTERACTION
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosUserImage = async (payload: FormData): Promise<FormData> => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  try {
    const response = await axiosPut<any>(
      ApiPaths.USER_MEDIA,
      payload,
      config,
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPutBannerImage = async (
  payload: FormData
): Promise<FormData> => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  try {
    const response = await axiosPut<any>(
      ApiPaths.USER_BANNER_MEDIA,
      payload,
      config,
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPutGroupImage = async (payload: FormData): Promise<any> => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  try {
    const response = await axiosPut(
      ApiPaths_Groups.GROUP_IMAGE_MEDIA,
      payload,
      config,
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const getActiveUser = async (): Promise<any> => {
  try {
    const response = await axiosGet<any>(
      ApiPaths.GET_ACTIVE_USER,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getNotificationCount =
  async (): Promise<INotificationCountResponse> => {
    try {
      const response = await axiosGet<INotificationCountResponse>(
        ApiPaths_Notification.NOTIFICATION_COUNT,
        {},
        BASE_API_NOTIFICATION
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };
export const axiosGroupProfile = async (
  payload: IGroupHeader
): Promise<any> => {
  try {
    const response = await axiosPut(
      ApiPaths_Groups.GROUP_PROFILE,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};
export const axiosGroupPutBannerImage = async (
  payload: FormData
): Promise<any> => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  try {
    const response = await axiosPut(
      ApiPaths_Groups.Group_BANNER_MEDIA,
      payload,
      config,
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosPostFriendsList = async (
  payload: any,
  postId: string
): Promise<any> => {
  try {
    const response = await axiosPost(
      `${ApiPaths.USER_PROFILE_FRIENDS_LIST}/${postId}`,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getCarousel = async (): Promise<any> => {
  try {
    const response = await axiosGet<any>(ApiPaths.USER_CAROUSEL, {}, BASE_API);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status !== 200) {
      throw getAPIError(error);
    }
    throw new Error(error.message || 'An error occurred while fetching topics');
  }
};

export const getFeatureFlag = async (): Promise<any> => {
  try {
    const response = await axiosGet<any>(ApiPaths.FEATURE_FLAG, {}, BASE_API);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const createOrUpdateUserStatusActiveStatus = async (
  payload?: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `${ApiPaths_Chat.UPDATE_USER_STATUS}`,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getAllConversation = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.GET_ALL_CONVERSATIONS,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getAllMessages = async (
  conversationId: string,
  payload:any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `${ApiPaths_Chat.GET_ALL_MESSAGES}/${conversationId}`,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getConversationDetails = async (
  conversationId: string
): Promise<any> => {
  try {
    const response = await axiosGet<any>(
      `${ApiPaths_Chat.GET_CONVERSATION_DETAILS}/${conversationId}`,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const createConversation = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.CREATE_CONVERSATION,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const sendMessage = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.SEND_MESSAGE,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const sendGroupInviteMessage = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.SEND_GROUP_INVITE_MESSAGE,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const updateMessageReaction = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.REACT_TO_MESSAGE,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const deleteMessage = async (
  conversationId: string,
  messageId: string
): Promise<any> => {
  try {
    const response = await axiosDelete<any>(
      `${ApiPaths_Chat.DELETE_MESSAGE}/${conversationId}/messages/${messageId}`,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const deleteConversation = async (
  conversationId: string
): Promise<any> => {
  try {
    const response = await axiosDelete(
      `${ApiPaths_Chat.DELETE_CONVERSATION}/${conversationId}`,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const addParticipantToConversation = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.ADD_PARTICIPANT,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const updateDeliveredStatus = async (
  payload?: any
): Promise<any> => {
  try {
    await axiosPost<any>(
      `${ApiPaths_Chat.UPDATE_DELIVERED_STATUS}`,
      payload,
      {},
      BASE_API_CHAT
    );
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const updateReadStatus = async (
  conversationId: string,
  payload?: any
): Promise<any> => {
  try {
    await axiosPost<any>(
      `${ApiPaths_Chat.UPDATE_READ_STATUS}/${conversationId}`,
      payload,
      {},
      BASE_API_CHAT
    );
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const signalrNegotiate = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `${ApiPaths_Chat.SIGNALR_NEGOTIATE}?userId=${payload}`,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const signalrConnect = async (
  payload: { userId: string; connectionId: string }
): Promise<any> => {
  try {
    const { userId, connectionId } = payload;
    const response = await axiosPost<any>(
      `${ApiPaths_Chat.SIGNALR_CONNECT}?userId=${userId}&connectionId=${connectionId}`,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getAllGroupConversation = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.GET_ALL_GROUP_CONVERSATIONS,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getConversationSnapShot = async (
  conversationId: string
): Promise<any> => {
  try {
    const response = await axiosGet<any>(
      `${ApiPaths_Chat.GET_CONVERSATIONS_SNAPSHOT}/${conversationId}`,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getGroupConversationSnapShot = async (
  groupConversationId: string
): Promise<any> => {
  try {
    const response = await axiosGet<any>(
      `${ApiPaths_Chat.GET_GROUP_CONVERSATIONS_SNAPSHOT}/${groupConversationId}`,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const getGroupMessages = async (
  groupConversationId: string,
  payload:any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `${ApiPaths_Chat.GET_GROUP_MESSAGES}/${groupConversationId}`,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const createGroupConversation = async (
  groupId: string,
  payload?: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `${ApiPaths_Chat.CREATE_GROUP_CONVERSATION}/${groupId}`,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const sendMessageToGroup = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.SEND_GROUP_MESSAGE,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

type PreviewRequest = {
  url: string;
  resolve: (preview: any) => void;
  reject: (error: any) => void;
};

const previewQueue: PreviewRequest[] = [];
const pendingPreviews = new Map<string, Promise<any>>();
const PREVIEW_BATCH_SIZE = 10;
let previewFlushScheduled = false;

const flushPreviewQueue = async () => {
  previewFlushScheduled = false;
  const requests = previewQueue.splice(0);
  for (let index = 0; index < requests.length; index += PREVIEW_BATCH_SIZE) {
    const batch = requests.slice(index, index + PREVIEW_BATCH_SIZE);
    try {
      const { data: previews } = await axiosPost<{ urls: string[] }, any[]>(
        `${ApiPaths_Chat.LINK_PREVIEW}`,
        { urls: batch.map(request => request.url) },
        {},
        BASE_API_CHAT
      );
      // Failed previews may be omitted; response positions need not match requests.
      const previewsByUrl = new Map<string, any>();
      previews.forEach(preview => {
        if (preview?.url) previewsByUrl.set(preview.url, preview);
      });
      batch.forEach(request => {
        request.resolve(previewsByUrl.get(request.url) ?? null);
      });
    } catch (error) {
      const apiError = getAPIError(error);
      batch.forEach(request => request.reject(apiError));
    } finally {
      batch.forEach(request => pendingPreviews.delete(request.url));
    }
  }
};

/** Batches queued URLs and shares results while a URL is queued or in flight. */
export const getLinkPreview = (link: string): Promise<any> => {
  const pending = pendingPreviews.get(link);
  if (pending) return pending;

  const preview = new Promise<any>((resolve, reject) => {
    previewQueue.push({ url: link, resolve, reject });
  });
  pendingPreviews.set(link, preview);
  if (!previewFlushScheduled) {
    previewFlushScheduled = true;
    setTimeout(flushPreviewQueue, 0);
  }
  return preview;
};

export const updateGroupMessageDeliveredStatus = async (
  payload?: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `${ApiPaths_Chat.UPDATE_GROUP_MESSAGE_DELIVERED_STATUS}`,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const updateGroupMessageReadStatus = async (
  groupConversationId: string,
  payload?: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      `${ApiPaths_Chat.UPDATE_GROUP_MESSAGE_READ_STATUS}/${groupConversationId}`,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const updateReactionToGroupMessage = async (
  payload: any
): Promise<any[]> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.REACT_TO_GROUP_MESSAGE,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const deleteMessageFromGroup = async (
  groupConversationId: string,
  messageId: string
): Promise<any> => {
  try {
    const response = await axiosDelete<any>(
      `${ApiPaths_Chat.DELETE_GROUP_MESSAGE}/${groupConversationId}/messages/${messageId}`,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const deleteGroupConversation = async (
  groupConversationId: string
): Promise<any> => {
  try {
    const response = await axiosDelete(
      `${ApiPaths_Chat.DELETE_GROUP_CONVERSATION}/${groupConversationId}`,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const reportConversation = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths_Chat.REPORT_CONVERSATION,
      payload,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const searchByMessage = async (
  searchText: string
): Promise<any> => {
  try {
    const response = await axiosGet<any>(
      `${ApiPaths_Chat.SEARCH_BY_MESSAGE}/${searchText}`,
      {},
      BASE_API_CHAT
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostDeletePost = async (postId: string , actionType:string): Promise<any> => {
  try {
    const ApiPath = ApiPaths_Post.DELETE_POST + '/' + postId + '/' + actionType;
    const response = await axiosDelete<any>(ApiPath , {
    },
    BASE_API_POST);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeleteUserImage = async (): Promise<FormData> => {
  try {
    const ApiPath =   ApiPaths.USER_MEDIA;
    const response = await axiosDelete<any>(
      ApiPath,
      {},
      BASE_API);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeleteUserBannerImage = async (): Promise<FormData> => {
  try {
    const ApiPath =   ApiPaths.USER_BANNER_MEDIA;
    const response = await axiosDelete<any>(
      ApiPath,
      {},
      BASE_API);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeleteGroupBannerImage = async (id:string): Promise<FormData> => {
  try {
    const ApiPath =   ApiPaths_Groups.DELETE_GROUP_BANNER+'/'+id;
    const response = await axiosDelete<any>(
      ApiPath);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeleteGroupImage = async (id:string): Promise<FormData> => {
  try {
    const ApiPath =   ApiPaths_Groups.DELETE_GROUP_IMAGE+'/'+id;
    const response = await axiosDelete<any>(
      ApiPath);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeleteCommentsPostId = async (
  commentId: string
): Promise<any> => {
  try {
    const response = await axiosDelete(
      `${ApiPaths.COMMENTS}/${commentId}`,
      {},
      BASE_API_INTERACTION
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPollParticipantsList = async (postId: string): Promise<any> => {
  try {
    const ApiPath = ApiPaths.User_Poll_Participants_List + '/' + postId;
    const response = await axiosGet<any>(ApiPath, {}, BASE_API_INTERACTION);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPinUnpinPost = async (postId: string, pinned: boolean, type?: string): Promise<any> => {
  try {
    const ApiPath = type === 'admin' ? ApiPaths_Post.ADMIN_PIN_UNPIN_POST : ApiPaths_Post.USER_PIN_UNPIN_POST;
    const requestBody = {
      postId,
      pinned
    };

    const response = await axiosPost<any>(ApiPath, requestBody, {}, type === 'admin' ? BASE_API_ADMIN : '');
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGroupPinUnpinPost = async (postId: string, groupId: string, pinned: boolean): Promise<any> => {
  try {
    const response = await axiosPost<any>( ApiPaths_Post.GROUP_PIN_UNPIN_POST,
    { postId, groupId, pinned }, {}
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosMediaList = async (userId: string, payload?: any): Promise<any> => {
  try {
    const ApiPath = ApiPaths.GET_USER_MEDIA + `/${userId}`;
    const response = await axiosPost<any>(ApiPath, payload? payload : {});
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosActivityList = async (userId: string, payload?: any): Promise<any> => {
  try {
    const ApiPath = ApiPaths.User_Activity + `/${userId}`;
    const response = await axiosPost<any>(ApiPath , payload? payload:{});
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostGetAllGroupsList = async (
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost(
      `${ApiPaths_Groups.ALL_GROUPS_LIST}`,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosAddUserToGroup = async (groupId: any, membersUserIds: any): Promise<any> => {
  try {
    const response = await axiosPut(
      `${ApiPaths_Groups.ADD_USER_TO_GROUP}${groupId}`,
      membersUserIds,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosRemoveUserFromGroup = async (groupId: any, membersUserIds: any): Promise<any> => {
  try {
    const response = await axiosPut(
      `${ApiPaths_Groups.REMOVE_USER_FROM_GROUP}${groupId}`,
      membersUserIds,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosPostGetAllUsersList = async (
  payload: any,
  postId: string
): Promise<any> => {
  try {
    const response = await axiosPost(
      `${ApiPaths.GET_ALL_USERS}/${postId}/potential-members`,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosPostGetGroupMembers = async (
  payload: any,
  groupId: string,
  keyword: string
): Promise<any> => {
  try {
    const response = await axiosPost(
      `${ApiPaths.GET_GROUP_MEMBERS}/${groupId}?keyword=${keyword}`,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGlobalReport = async (
  path: string,
  payload: FormData,
): Promise<FormData> => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  try {
    const response = await axiosPost<any>(path, payload, config, BASE_API);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosLogout = async (): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths.LOG_OUT,
      {},
      undefined,
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCreateGroupRelation = async (groupId: string, parentGroupIds: string[]): Promise<any> => {

  const payload = {
    groupId: groupId,
    parentGroupIds: parentGroupIds
  };

  try {
    const response = await axiosPost(
      ApiPaths_Groups.CREATE_GROUP_RELATION,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosDeleteGroupRelation = async (groupId: any, groupRelationId: any): Promise<any> => {

  const payload = {
    groupId: groupId,
    groupRelationId: groupRelationId
  };

  try {
    const response = await axiosPost(
      ApiPaths_Groups.DELETE_GROUP_RELATION,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (error: any) {
    throw getAPIError(error);
  }
};

export const axiosGetRelatedParentGroups = async (groupId: string): Promise<any> => {
  try {
    const response = await axiosGet<any>(`${ApiPaths_Groups.GET_RELATED_GROUPS}/${groupId}`, {}, BASE_API_POST);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetRelatedChildGroups = async (
  urlPath: string,
  payload: any
): Promise<any> => {
  try {
    const response = await axiosPost<any>(urlPath, payload, {}, BASE_API_POST);
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetPostsByHashTag = async (
  payload: any,
  keyword: string
): Promise<any> => {
  try {
    const response = await axiosPost(
      `${ApiPaths_Post.SEARCH_BY_HASHTAG}/${keyword}`,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetHashTagSuggestions = async (
  keyword: string
): Promise<any> => {
  try {
    const response = await axiosGet(
      `${ApiPaths_Post.GET_HASHTAGS}/${keyword}`,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const searchPostsByKeyword = async (
  payload: { size: number; nextPageCursor: string; previousPageCursor: string },
  keyword: string
): Promise<any> => {
  try {
    const response = await axiosPost(
      `${ApiPaths_Post.SEARCH_BY_KEYWORD}/${encodeURIComponent(keyword)}`,
      payload,
      {},
      BASE_API_POST
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosGetAdminDeleteRequestList = async (payload: {
  page: number;
  size: number;
  statuses: string;
}): Promise<IAdminDeleteRequest[]> => {
  try {
    const response = await axiosGet<IAdminDeleteRequest[]>(
      ApiPaths.ADMIN_DELETE_REQUEST,
      {
        params: payload
      },
      BASE_API_ADMIN
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};
export const axiosGetExternalLinks = async (userId: string): Promise<IExternalLinkResponse[]> => {
  console.log('GET External Links URL:', `${ApiPaths.EXTERNAL_LINKS}/${userId}`);
  try {
    const response = await axiosGet<IExternalLinkResponse[]>(
      `${ApiPaths.EXTERNAL_LINKS}/${userId}`,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosCreateExternalLink = async (
  payload: IExternalLinkPayload
): Promise<any> => {
  try {
    const response = await axiosPost<any>(
      ApiPaths.CREATE_EXTERNAL_LINK,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosUpdateExternalLink = async (
  payload: IExternalLinkPayload
): Promise<any> => {
  try {
    const response = await axiosPut<any>(
      ApiPaths.EXTERNAL_LINKS,
      payload,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};

export const axiosDeleteExternalLink = async (id: string): Promise<any> => {
  try {
    const response = await axiosDelete<any>(
      `${ApiPaths.EXTERNAL_LINKS}/${id}`,
      {},
      BASE_API
    );
    return response.data;
  } catch (err: any) {
    throw getAPIError(err);
  }
};
